// src/components/PdfViewer.jsx
import React, { useEffect, useState, useRef } from 'react';

/**
 * Web PdfViewer component inspired by your React Native version.
 *
 * Props:
 *   - url (string)                 : primary blob/object URL or remote URL (your App currently passes `url`)
 *   - uri (string)                 : alternative name for the same (keeps parity with RN version)
 *   - remoteFallbackUrl (string)   : remote publicly-accessible fallback URL (optional)
 *
 * Behavior (keeps the same logic as your RN component):
 *  - Maintains an internal source object { type: 'file'|'remote', uri }.
 *  - Initially tries to load the provided url/uri.
 *  - If the iframe fails to load (onError), it will switch to:
 *      1) Google Docs viewer (if remoteFallbackUrl provided),
 *      2) remoteFallbackUrl (if provided),
 *      3) otherwise logs a warning.
 *
 * Layout:
 *  - Mirrors your RN styling: wrapper width 100%, height ~80vh, 1px border.
 *  - iframe fills the container (border: none).
 *
 * Note: iframe onError is not 100% reliable for all failure modes (CORS, different
 * browsers behave slightly differently). This implementation follows your RN logic
 * (attempt initial, on error try remote fallbacks) without adding extra network checks.
 */

export default function PdfViewer({ url, uri, remoteFallbackUrl }) {
  // prefer uri prop (RN parity), but support url (your App uses `url`)
  const initialUri = uri || url || '';
  const [useSource, setUseSource] = useState({ type: 'file', uri: initialUri });
  const [viewerHeight, setViewerHeight] = useState(Math.round(window.innerHeight * 0.8));
  const iframeRef = useRef(null);

  useEffect(() => {
    setUseSource({ type: 'file', uri: initialUri });
  }, [initialUri]);

  // update height on resize to emulate 80vh behaviour
  useEffect(() => {
    const onResize = () => setViewerHeight(Math.round(window.innerHeight * 0.8));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Construct Google Docs viewer URL if remote fallback is provided
  const googleDocsViewer = remoteFallbackUrl
    ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(remoteFallbackUrl)}`
    : null;

  // iframe onError handler: switch to remote fallback(s) similarly to RN version
  const handleIframeError = (e) => {
    // If we're already showing remote fallback, nothing to do
    if (useSource.type === 'remote') return;

    if (googleDocsViewer) {
      setUseSource({ type: 'remote', uri: googleDocsViewer });
      return;
    }

    if (remoteFallbackUrl) {
      setUseSource({ type: 'remote', uri: remoteFallbackUrl });
      return;
    }

    // No fallback available — log for developer
    // (we don't change user-visible UI beyond keeping the iframe which failed)
    // This mirrors RN behavior where it logs a warning and stops.
    // eslint-disable-next-line no-console
    console.warn('PdfViewer: iframe failed to load file and no remote fallback available.', e?.nativeEvent ?? e);
  };

  // small defensive measure: when the source changes to a file blob URL we ensure it exists
  // (we do not fetch or modify logic — simply keep the same source)
  useEffect(() => {
    // nothing to do other than ensure iframe reloads when useSource.uri changes
    if (iframeRef.current) {
      // force reload by resetting src (some browsers require this to pick up blob changes)
      iframeRef.current.src = useSource.uri || 'about:blank';
    }
  }, [useSource.uri]);

  return (
    <div
      style={{
        width: '100%',
        height: viewerHeight,
        border: '1px solid #ddd',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <iframe
        ref={iframeRef}
        title="PDF Viewer"
        src={useSource.uri}
        style={{ width: '100%', height: '100%', border: 'none' }}
        onError={handleIframeError}
      />
    </div>
  );
}